import {Component, OnInit, ChangeDetectionStrategy, Inject, ViewChild, AfterViewInit, ChangeDetectorRef} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {BehaviorSubject, combineLatest, EMPTY, merge, Observable, of, Subject, timer} from 'rxjs';
import {catchError, filter, finalize, map, shareReplay, skip, startWith, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {MatStepper, MatStep} from '@angular/material/stepper';

import {TerraNumberPipe} from '../../../pipes/TerraNumber.pipe';
import {PlaidService} from '../../../../services/shared/plaid.service';
import {AchDebitCreateDto} from '../../../models/cre-banking/plaid/AchDebitCreateDto.model';
import {TerraUtils} from '../../../TerraUtils';
import {SnackbarService} from '../../../../services/snackbar.service';
import {LoggerService} from '../../../errors/logger.service';
import {PlaidDataService} from '../../../../services/shared/plaid-data.service';
import {CustomValidators} from '../../../validators/custom.validators';
import {BaseResponseDto} from '../../../models/BaseResponseDto.model';
import {UtilsService} from '../../../../services/utils.service';
import {UnitBankAccountService} from '../unit-bank-account/unit-bank-account.service';
import {DropdownChangeEvent, DropdownFilterOptions} from 'primeng/dropdown';
import _ from 'lodash';
import {ExternalClientBankAccountDto} from 'src/app/dashboard/bankAccounts/models/externalClientBankAccountDto.model';
import {DialogService} from 'src/app/services/dialog.service';
import {ConfirmDialogParams} from '../../confirm-dialog/confirm-dialog.component';

export interface TransferUsingPlaidParams {
  unitBankAccountId: number;
  unitBankAccountName: string;
  holdingId: number;
  continueOAuthFlow: boolean;
  displayAchDebitLimits: boolean;
  amount: number;
  lockAmount: boolean;
  title: string;
  amountStepButtonText: string;
  paymentToken: string;
  paymentDescription: string;
  paymentAddenda: string;
  accountHolderDefault: string;
}

@Component({
  selector: 'terra-transfer-using-plaid-modal',
  templateUrl: './transfer-using-plaid-dialog.component.html',
  styleUrls: ['./transfer-using-plaid-dialog.component.scss'],
  providers: [PlaidService, PlaidDataService, UnitBankAccountService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TransferUsingPlaidDialogComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
  @ViewChild('stepper') private stepper: MatStepper;

  private readonly _idempotencyKey: string;
  readonly linkVerificationStatus = 99
  readonly autoVerificationPendingStatus = this.plaidService.autoVerificationPendingStatus;
  readonly manualVerificationPendingStatus = this.plaidService.manualVerificationPendingStatus;
  readonly shouldNotAskForHolderName = [this.linkVerificationStatus,
  this.autoVerificationPendingStatus,
  this.manualVerificationPendingStatus]
  amountForm: UntypedFormGroup;
  bankAccountForm: UntypedFormGroup;
  filterValue: string = '';
  amountErrors: {
    errorName: string,
    description: string
  }[] = [
      {
        errorName: 'required',
        description: 'Required'
      },
      {
        errorName: 'min',
        description: 'Amount should be greater than 0'
      },
      {
        errorName: 'maxFormattedNumber',
        description: 'Amount exceeds the limit'
      },
      {
        errorName: 'minFormattedNumber',
        description: 'Amount should be greater than 0'
      }
    ];
  linkedBankAccounts$ = this.plaidService.getLinkedPlaidBankAccounts(this.data.holdingId, false, this.data.unitBankAccountId).pipe(
    untilComponentDestroyed(this),
    tap(_ => this.hasErrorReceivingLinkedBankAccounts$.next(false)),
    tap(accounts => {
      const authAccount = this.plaidService.lastSuccessfulAuthAccount();
      if (!authAccount) {
        this.bankAccountForm.get('bankAccount').setValue(this.getDefaultAccount(accounts));
        return;
      }
      const authAccountFromAccounts = accounts.find(acc => acc.externalAccountId === authAccount);
      if (!authAccountFromAccounts) {
        return;
      }
      this.bankAccountForm.get('bankAccount').setValue(authAccountFromAccounts);
      this.bankAccountForm.get('bankAccount').updateValueAndValidity();
      this.plaidService.lastSuccessfulAuthAccount.set('');
    }),
    catchError(err => {
      if (err instanceof BaseResponseDto) {
        this.utilsService.alertErrorMessage(err);
      } else {
        this.logger.error('An error occurred getting linked bank accounts', err);
        this.hasErrorReceivingLinkedBankAccounts$.next(true);
      }

      return EMPTY;
    }),
    shareReplay(1)
  );

  linkedBankAccountsGroup$ = this.linkedBankAccounts$.pipe(
    map(accounts => _.chain(accounts)
      .groupBy((item) => this.data.holdingId == item.holdingId ? 'First' : 'Last')
      .pick(['First', 'Last'])
      .map((items, key) => ({name: key, items: items}))
      .value()));

  bankAccountLimits$ = this.unitBankAccountService.getBankAccountAchDebitLimit(this.data.unitBankAccountId).pipe(
    shareReplay(1)
  );

  // error indicators
  hasErrorReceivingLinkedBankAccounts$ = new BehaviorSubject<boolean>(false);
  hasErrorReceivingLinkToken$ = new BehaviorSubject<boolean>(false);
  hasErrorSubmittingTransfer$ = new BehaviorSubject<boolean>(false);

  // loaders
  isLoadingBankAccounts$ = combineLatest([this.linkedBankAccounts$.pipe(startWith(null)),
  this.hasErrorReceivingLinkedBankAccounts$,
  this.plaidService.isRefreshingLinkedBankAccounts$]).pipe(
    map(([linkedBankAccounts, hasErrors, isRefreshingLinkedBankAccounts]) =>
      !hasErrors && (!linkedBankAccounts || isRefreshingLinkedBankAccounts))
  );
  isLoadingBalance$ = new BehaviorSubject<boolean>(false);
  currentAccountBalance$ = new BehaviorSubject<number>(null);
  loadingBalanceError$ = new BehaviorSubject<string>(null);
  isLoadingBalanceHasError$ = this.loadingBalanceError$.pipe(
    map(val => !!val)
  );


  stopTimer$ = new Subject<void>();
  confirmButtonTimer$ = this.isLoadingBalance$
    .pipe(filter(loading => !!loading),
      switchMap(_ => timer(15_000).pipe(takeUntil(this.stopTimer$), tap(d => this.logger.warning(`transfer with plaid => 15 seconds have passed and the balance has not yet returned`)))),
      map(_ => false), shareReplay(1));


  canMakeTransfer$: Observable<boolean> = of(false);

  hideInsufficientAmount$ = merge(of(true), this.isLoadingBalance$.pipe(skip(1)));
  insufficientAmount$ = of(false);


  isSubmitting$ = new BehaviorSubject<boolean>(false);
  submitErrorText = ``;

  disabledConfirmButton$ = merge(of(true), this.confirmButtonTimer$, this.isLoadingBalance$.pipe(skip(1)));

  constructor(public dialogRef: MatDialogRef<TransferUsingPlaidDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TransferUsingPlaidParams,
    private plaidService: PlaidService,
    private fb: UntypedFormBuilder,
    private numberPipe: TerraNumberPipe,
    private snackbarService: SnackbarService,
    private logger: LoggerService,
    private changeDetectorRef: ChangeDetectorRef,
    private unitBankAccountService: UnitBankAccountService,
    private utilsService: UtilsService,
    private dialogService: DialogService) {
    super();
    this._idempotencyKey = TerraUtils.uuidv4();
  }

  get hasAmount() {
    return !!this.numberPipe.parse(this.amountForm.get('amount').value)
  }

  ngOnInit(): void {
    this.dialogRef.addPanelClass('transfer-with-plaid-dialog');
    this.generateForms();
    this.setAmountValidators();
    this.subscribeToFormUpdates();
    this.canMakeTransfer$ = combineLatest([this.amountValueChanges,
    this.bankAccountForm.get('bankAccount').valueChanges,
    this.isLoadingBalanceHasError$]).pipe(
      map(([amount, bankAccount, hasError]) => {
        return !hasError && !!bankAccount && !!amount
      }), shareReplay(1)
    );

    this.insufficientAmount$ = combineLatest([this.amountValueChanges, this.currentAccountBalance$])
      .pipe(map(([valueChange, currentAccountBalance]) => {
        const insufficientAmount = currentAccountBalance !== null && this.numberPipe.parse(valueChange) > currentAccountBalance;
        if (insufficientAmount) {
          this.logger.warning('transfer with plaid => The customer chose a balance lower than the transfer amount');
        }
        return insufficientAmount;
      }), shareReplay(1))
  }

  get amountValueChanges() {
    // startWith() -> because in Capital call is disabled
    return this.amountForm.get('amount').valueChanges.pipe(startWith(this.amountForm.get('amount').value));
  }

  ngAfterViewInit(): void {
    // continue OAuth flow after signing in to bank's website
    if (this.data.continueOAuthFlow) {
      this.stepper.next();
      this.changeDetectorRef.detectChanges();
      this.openPlaidLink();
    }
  }

  /**
   * Generates forms
   */
  private generateForms() {
    this.amountForm = this.fb.group({
      amount: [null, [Validators.required, CustomValidators.minFormattedNumber(0)]],
    });
    this.bankAccountForm = this.fb.group({
      bankAccount: [null, Validators.required],
      accountHolder: [null]
    });

    if (!!this.data.amount) {
      this.amountForm.get('amount').setValue(this.data.amount);
    }

    if (this.data.lockAmount) {
      this.amountForm.get('amount').disable();
    }
  }

  private getDefaultAccount(accounts: ExternalClientBankAccountDto[]) {
    if (accounts.length != 1) {
      return accounts.find(acc => acc.isRecent);
    }
    if ([this.autoVerificationPendingStatus, this.manualVerificationPendingStatus].includes(accounts[0].verificationStatus)) {
      return null;
    }
    return accounts[0];
  }

  accountSelected(event: DropdownChangeEvent) {
    if (!event.value) {
      return;
    }
    const selectedAccount = event.value as ExternalClientBankAccountDto;
    if (selectedAccount.verificationStatus === this.autoVerificationPendingStatus) {
      this.dialogService.confirmDialog(new ConfirmDialogParams('Account Linking Not Yet Completed', 'You cannot fund your account using this bank account just yet. The connectivity process is still in progress and usually completes within a few hours. Once the process is completed, you will be able to proceed with funding your account.', 'OK', 'Delete Account'))
        .afterClosed()
        .pipe(
          take(1),
          switchMap(result => result ? of(null) : this.deleteAccount(selectedAccount)))
        .subscribe();
      this.bankAccountForm.reset();
    }

    if (selectedAccount.verificationStatus === this.manualVerificationPendingStatus) {
      this.dialogService.confirmDialog(
        new ConfirmDialogParams(
          'Account Linking Not Yet Completed',
          'You cannot fund your account using this bank account just yet. The connectivity process is still in progress and typically takes 1-2 business days. You will receive an SMS from Plaid with a link to verify your account by entering the micro-deposit amounts sent to your bank. Once this process is completed, you will be able to proceed with funding your account.',
          'OK', 'Delete Account', null, null, 'Finish Verification Process', 'primary', null, null, 'left', null, null, null, false, '#FFF5DD', null, false))
        .afterClosed()
        .pipe(
          take(1),
          filter(result => typeof result === 'boolean' || result?.secondaryAction),
          map(result => result?.secondaryAction ? "Plaid" : result ? "Close" : "Delete"),
          switchMap(result => result == "Plaid" ? of(this.continuePlaidProcess(selectedAccount)) : result === "Delete" ? this.deleteAccount(selectedAccount) : of(null)))
        .subscribe();
      this.bankAccountForm.reset();
    }
  }

  deleteAccount(account: ExternalClientBankAccountDto) {
    return this.plaidService.deleteAccessToken(account.accessTokenId);
  }

  continuePlaidProcess(account: ExternalClientBankAccountDto) {
    this.plaidService.openPlaidLink(account.accessTokenId);
  }

  /**
   * Subscribes to forms updates and act accordingly
   */
  private subscribeToFormUpdates() {
    this.loadingBalanceError$.next(null);
    this.currentAccountBalance$.next(null);
    this.bankAccountForm.get('bankAccount').valueChanges.pipe(
      untilComponentDestroyed(this),
      filter(acc => !!acc),
      tap(_ => this.isLoadingBalance$.next(true)),
      tap(acc => {
        if (this.shouldNotAskForHolderName.includes(acc.verificationStatus)) {
          this.bankAccountForm.get('accountHolder').setValue(null);
          this.bankAccountForm.get('accountHolder').clearValidators();
        } else {
          this.bankAccountForm.get('accountHolder').setValue(acc.holderFullName || this.data.accountHolderDefault);
          this.bankAccountForm.get('accountHolder').addValidators(Validators.required);
        }
        this.bankAccountForm.get('accountHolder').updateValueAndValidity();
      }),
      switchMap(acc => acc?.availableBalance !== null ? of(acc.availableBalance)
        : acc.verificationStatus !== this.linkVerificationStatus ? of(null) :
          this.plaidService.getLinkedPlaidBankAccountBalance(acc).pipe(catchError(err => {
            this.isLoadingBalance$.next(false);
            this.loadingBalanceError$.next("An Error has occured while fetching balance");
            return EMPTY;
          }))),
      tap(b => {
        this.stopTimer$.next();
        this.isLoadingBalance$.next(false);
        this.currentAccountBalance$.next(b);
      }),
    ).subscribe({
      next: _ => {
        this.loadingBalanceError$.next(null)
        this.changeDetectorRef.detectChanges();
      },
      error: err => {
        this.isLoadingBalance$.next(false);
        this.currentAccountBalance$.next(null);
        this.loadingBalanceError$.next("An Error has occured while fetching balance");
      }
    })
  }

  /**
   * Checks if amount step is in completed STATUS
   */
  get isAmountStepCompleted(): boolean {
    this.amountForm.updateValueAndValidity();
    return this.amountForm.valid || this.amountForm.disabled;
  }

  /**
   * Opens Plaid link GUI
   */
  public openPlaidLink() {
    this.plaidService.openPlaidLink();
  }

  /**
   * Sends a request to the server for transferring funds from linked bank account
   */
  public fundAccount() {
    this.bankAccountForm.updateValueAndValidity();
    this.amountForm.updateValueAndValidity();
    if (this.bankAccountForm.invalid || this.amountForm.invalid) {
      return;
    }

    this.hasErrorSubmittingTransfer$.next(false);
    this.submitErrorText = ``;
    const fundAccountRequestData = new AchDebitCreateDto();
    fundAccountRequestData.amount = this.numberPipe.parse(this.amountForm.get('amount').value);
    fundAccountRequestData.clientBankAccountId = this.bankAccountForm.get('bankAccount').value.id;
    fundAccountRequestData.destinationUnitBankAccountId = this.data.unitBankAccountId;
    fundAccountRequestData.idempotencyKey = this._idempotencyKey;
    fundAccountRequestData.holdingId = this.data.holdingId;
    fundAccountRequestData.paymentToken = this.data.paymentToken;
    fundAccountRequestData.description = this.data.paymentDescription;
    fundAccountRequestData.addenda = this.data.paymentAddenda;
    fundAccountRequestData.externalAccountId = this.bankAccountForm.get('bankAccount').value.externalAccountId;
    fundAccountRequestData.accessTokenId = this.bankAccountForm.get('bankAccount').value.accessTokenId;
    fundAccountRequestData.beneficiaryName = this.bankAccountForm.get('accountHolder').value;

    this.isSubmitting$.next(true);
    this.plaidService.transfer(fundAccountRequestData).pipe(
      take(1),
      finalize(() => this.isSubmitting$.next(false))
    ).subscribe(_ => {
      this.snackbarService.showGeneralMessage('Account funded successfully', 5);
      this.dialogRef.close(true);
    },
      error => {
        this.logger.error('An error occurred funding account', error);
        this.submitErrorText = typeof error.error === 'string' ? error.error.trimStart() : ``;
        this.hasErrorSubmittingTransfer$.next(true);
      });
  }

  /**
   * Checks if a bank account has sufficient balance for creating a transfer
   * @param bankAccount The bank account to check
   */
  public isBankAccountHasSufficientFunds(balance: number): boolean {
    return this.numberPipe.parse(this.amountForm.get('amount').value) <= balance;
  }

  /**
   * Get icon name for step based on the stepper state
   * @param step The step
   */
  public getIconName(step: MatStep): string {
    if (step._stepper.selected === step) {
      return 'adjust';
    } else if (step.completed || step.stepControl?.disabled) {
      return 'done';
    } else {
      return 'radio_button_unchecked';
    }
  }


  private setAmountValidators() {
    if (this.data.displayAchDebitLimits) {
      this.bankAccountLimits$.pipe(
        untilComponentDestroyed(this),
        filter(limits => !!limits && !!limits.achLimits)
      ).subscribe(limits => {
        this.amountForm.get('amount').setValidators([Validators.required, Validators.min(0.01),
        CustomValidators.maxFormattedNumber(limits.achLimits.maximumDebitAllowed / 100)]);
      });
    }
  }

  resetFunction(options: DropdownFilterOptions) {
    options.reset();
    this.filterValue = '';
  }

  customFilterFunction(event: string, options: DropdownFilterOptions) {
    const eventStructured = {target: {value: event}};
    options.filter(eventStructured);
  }
}
